import type { ReactElement, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { CHART_TYPES } from './catalog'
import type { ChartTypeId } from './types'

// Chart-shape picker — inline icon row (no dropdown), TradingView style and
// order: bar · candle · heikin ashi · line. Icons are hand-drawn to match TV's glyphs.
function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  )
}
// OHLC bars: two vertical bars with open (left) / close (right) ticks.
function BarIcon() {
  return (
    <Svg>
      <line x1="8" y1="4" x2="8" y2="15" />
      <line x1="5" y1="7" x2="8" y2="7" />
      <line x1="8" y1="11" x2="11" y2="11" />
      <line x1="16" y1="9" x2="16" y2="20" />
      <line x1="13" y1="12" x2="16" y2="12" />
      <line x1="16" y1="16" x2="19" y2="16" />
    </Svg>
  )
}
// Candlesticks: two outlined bodies with wicks.
function CandleIcon() {
  return (
    <Svg>
      <rect x="5.5" y="7" width="5" height="8" rx="0.5" />
      <line x1="8" y1="3.5" x2="8" y2="7" />
      <line x1="8" y1="15" x2="8" y2="18.5" />
      <rect x="13.5" y="10" width="5" height="7" rx="0.5" />
      <line x1="16" y1="6" x2="16" y2="10" />
      <line x1="16" y1="17" x2="16" y2="21" />
    </Svg>
  )
}
// Heikin Ashi: two outlined candles, each with a top wick only (no bottom wick).
// The second is smaller and steps up, its base at the midpoint of the first.
function HeikinIcon() {
  return (
    <Svg>
      {/* candle 1: body 10–19, top wick only */}
      <rect x="6" y="10" width="5" height="9" rx="0.5" />
      <line x1="8.5" y1="6" x2="8.5" y2="10" />
      {/* candle 2: smaller, base at candle 1's midpoint (≈14.5), top wick only */}
      <rect x="14" y="8" width="4" height="6.5" rx="0.5" />
      <line x1="16" y1="4.5" x2="16" y2="8" />
    </Svg>
  )
}
// Line / area: a zigzag polyline.
function LineIcon() {
  return (
    <Svg>
      <polyline points="4,15 9,9 13,12 20,5" />
    </Svg>
  )
}

const ICONS: Record<ChartTypeId, () => ReactElement> = {
  bar: BarIcon,
  candle: CandleIcon,
  heikin: HeikinIcon,
  line: LineIcon,
}

export interface WorkbenchChartTypeProps {
  value: ChartTypeId
  onChange: (id: ChartTypeId) => void
}

export function WorkbenchChartType({ value, onChange }: WorkbenchChartTypeProps) {
  return (
    <div className="flex items-center gap-0.5">
      {CHART_TYPES.map((t) => {
        const Icon = ICONS[t.id]
        const on = t.id === value
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            title={t.label}
            aria-label={t.label}
            aria-pressed={on}
            className={cn(
              'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors cursor-pointer',
              on ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
          >
            <Icon />
          </button>
        )
      })}
    </div>
  )
}
